<template>
  <ValidationObserver ref="policyDetailsViewForm">
    <div class="card card-custom">
        <div class="card-body p-0">

          <div class="px-8 pt-6 px-lg-15">
            <button
              class="btn btn-sm btn-light-primary font-weight-bold text-uppercase mr-2"
              @click.prevent="$router.push({ path: '/workbench', query: { lane: 'maintenance' } })"
            >
              ← Work Queue
            </button>
            <button
              v-if="policy && policy.K_Client && policy.K_Client.id"
              class="btn btn-sm btn-light font-weight-bold text-uppercase"
              @click.prevent="$router.push(`/workbench/clients/${policy.K_Client.id}`)"
            >
              Open client
            </button>
          </div>

          <!--begin: Wizard-->
          <div id="policyDetailsViewFormWizard" class="wizard wizard-3" data-wizard-state="step-first">

            <!--begin: Wizard Nav -->
            <div v-show="policy" class="wizard-nav border-bottom mb-1 mb-lg-5">
              <div class="wizard-steps px-8 py-8 px-lg-15 py-lg-3">
                <div v-for="(wizardPage, index) in wizardPages" :key="index" class="wizard-step" data-wizard-type="step">
                  <div class="wizard-label" style="cursor: pointer;">
                    <h3 class="wizard-title text-center">{{ wizardPage.title }}</h3>
                    <div class="wizard-bar"></div>
                  </div>
                </div>
              </div>
            </div>
            <!--end: Wizard Nav -->

            <!--begin: Wizard Body-->
            <div class="card card-custom card-shadowless rounded-top-0">
              <div class="card-body p-0">
                <div class="row justify-content-center py-8 px-8 py-lg-10 px-lg-10">
                  <div class="col-xl-12 col-xxl-10">
                    <!--begin: Wizard Form-->
                    <div class="form mt-0">

                      <!--begin: ERROR ALERT-->
                      <b-alert v-if="errorAlert" variant="warning" show>
                        <div class="text-dark" v-html="errorAlert"></div>
                      </b-alert>
                      <!--end: ERROR ALERT-->

                      <div class="pb-5" data-wizard-type="step-content" data-wizard-state="current">
                        <overview-page v-if="policy" :policy="policy"/>
                      </div>

                      <div class="pb-5" data-wizard-type="step-content" >
                        <inclusions-page v-if="policy" :inclusions="policy.inclusions" :brand="policy.K_Brand"
                                         :loading-inclusions="loadingInclusions" @exportInclusions="exportInclusions"/>
                      </div>

                      <div class="pb-5" data-wizard-type="step-content">
                        <notes-page v-if="policy" :notes="policy.notes" :loading-notes="loadingNotes"/>
                      </div>

                      <div class="pb-5" data-wizard-type="step-content">
                        <tasks-page v-if="policy" :tasks="policy.tasks" :loading-tasks="loadingTasks"/>
                      </div>

                      <div class="pb-5" data-wizard-type="step-content">
                        <documents-page v-if="policy" :folder-shared-link="policy.policyBoxFolderSharedLink" :loading-documents="loadingDocuments"/>
                      </div>

                    </div>
                    <!--end: Wizard Form-->
                  </div>
                </div>
              </div>
            </div>
            <!--end: Wizard Body-->
          </div>
          <!--end: Wizard-->
        </div>

    </div>
  </ValidationObserver>
</template>

<style lang="scss">
@import "@/assets/sass/pages/wizard/wizard-3.scss";

.page-loading {
  display: flex;
  justify-content: center;
  align-items: center;
}

.step-disabled {
  cursor: not-allowed !important;
}

body.swal2-shown > [aria-hidden="true"] {
  transition: 0.1s filter;
  filter: blur(10px);
}

</style>

<script>
import _ from "lodash";
import moment from "moment-timezone";
import ApiService from "@/core/services/api.service";
import BrandService from "@/core/services/brand.service";
import KTWizard from "@/assets/js/components/wizard";
import KTUtil from "@/assets/js/components/util";
import {ADD_BODY_CLASSNAME, REMOVE_BODY_CLASSNAME} from "@/core/services/store/htmlclass.module";
import OverviewPage from "@/view/pages/wizard/policy-details-view/pages/OverviewPage.vue";
import InclusionsPage from "@/view/pages/wizard/policy-details-view/pages/InclusionsPage.vue";
import NotesPage from "@/view/pages/wizard/policy-details-view/pages/NotesPage.vue";
import TasksPage from "@/view/pages/wizard/policy-details-view/pages/TasksPage.vue";
import DocumentsPage from "@/view/pages/wizard/policy-details-view/pages/DocumentsPage.vue";

export default {
  name: 'PolicyDetailsView',

  components: {DocumentsPage, TasksPage, OverviewPage, InclusionsPage, NotesPage },

  data() {
    return {
      policyId: null,
      errorAlert: null,
      policy: null,

      loadingInclusions: false,
      loadingNotes: false,
      loadingTasks: false,
      loadingDocuments: false
    };
  },

  mounted() {
    this.startPageLoading();

    // Extract information from URL params
    const queryParams = this.$route.query;
    if (!queryParams.policyId) {
      this.handleError("Unauthorised access!");
      return;
    }
    this.policyId = queryParams.policyId;

    this.initWizard();

    // Get policy overview
    this.getPolicyData();
  },

  methods: {
    getPolicyData: function () {
      this.startPageLoading();
      ApiService.get(`/policy-details/${this.policyId}/overview`)
          .then(({ data }) => {
            this.policy = data.result;

            // Set UI Brand
            BrandService.setUIBrand(this.policy.K_Brand);

            // Set wizard title and subtitle
            const policyStartDate = moment.tz(this.policy.K_PolicyStartDate, 'YYYY-MM-DD', 'Australia/Sydney').format('DD/MM/YYYY');
            const policyEndDate = moment.tz(this.policy.K_PolicyEndDate, 'YYYY-MM-DD', 'Australia/Sydney').format('DD/MM/YYYY');
            const title = `${this.policy.K_PolicyNumber} (${policyStartDate} to ${policyEndDate})`;
            this.$store.commit("setWizardTitle", title);
            this.$store.commit("setWizardSubtitle", this.policy.K_Client.name);

            setTimeout(() => {
              this.stopPageLoading();
            }, 0);

            this.getInclusions();
            this.getNotes();
            this.getTasks();
            this.getDocumentsLink();
          })
          .catch((error) => {
            this.handleError(error);
          });
    },

    getInclusions: function () {
      this.loadingInclusions = true;
      ApiService.get(`/policy-details/${this.policyId}/inclusions`)
          .then(({ data }) => {
            this.$set(this.policy, 'inclusions', data.result);
            this.loadingInclusions = false;
          })
          .catch((error) => {
            this.loadingInclusions = false;
            console.log(error);
            this.handleError(`Error occurred while loading policy inclusions!`);
          });
    },

    getNotes: function () {
      this.loadingNotes = true;
      ApiService.get(`/policy-details/${this.policyId}/notes`)
          .then(({ data }) => {
            this.$set(this.policy, 'notes', data.result);
            this.loadingNotes = false;
          })
          .catch((error) => {
            this.loadingNotes = false;
            console.log(error);
            this.handleError(`Error occurred while loading notes!`);
          });
    },

    getTasks: function () {
      this.loadingTasks = true;
      ApiService.get(`/policy-details/${this.policyId}/tasks`)
          .then(({ data }) => {
            this.$set(this.policy, 'tasks', data.result);
            this.loadingTasks = false;
          })
          .catch((error) => {
            this.loadingTasks = false;
            console.log(error);
            this.handleError(`Error occurred while loading tasks!`);
          });
    },

    getDocumentsLink: function () {
      this.loadingDocuments = true;
      ApiService.get(`/policy-details/${this.policyId}/documents-link`)
          .then(({ data }) => {
            this.$set(this.policy, 'policyBoxFolderSharedLink', data.result);
            this.loadingDocuments = false;
          })
          .catch((error) => {
            this.loadingDocuments = false;
            console.log(error);
            this.handleError(`Error occurred while loading documents!`);
          });
    },

    exportInclusions: function () {
      this.startPageLoading();

      ApiService.post(`/policy-details/${this.policyId}/inclusions/export`)
          .then(({ data }) => {
            this.stopPageLoading();

            // Decode the base-64 string into a binary string
            const binaryString = window.atob(data.result);
            const len = binaryString.length;

            // Convert binary string to an array of bytes
            const bytes = new Uint8Array(len);
            for (let i = 0; i < len; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }

            // Create a Blob from the byte array with the proper MIME type for Excel files
            const blob = new Blob([bytes], {
              type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });

            // Create a temporary link element
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = `${this.policy.K_PolicyNumber} - Inclusions.xlsx`;

            // Append the link to the document, trigger the download, then remove the link
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Revoke the object URL to free memory
            window.URL.revokeObjectURL(link.href);
          })
          .catch((error) => {
            this.handleError(error);
          });
    },

    initWizard: function () {
      // Initialize form wizard
      const wizard = new KTWizard('policyDetailsViewFormWizard', {
        startStep: 1, // initial active step number
        clickableSteps: true, // allow step clicking
      });

      this.wizardObj = wizard;

      // Change event - Scroll to top of the page
      wizard.on("change", (/*wizardObj*/) => {
        setTimeout(function () {
          KTUtil.scrollTop();
        }, 500);
      });
    },

    setError: function (message) {
      KTUtil.scrollTop();
      this.errorAlert = message;
    },

    clearError: function () {
      this.errorAlert = null;
    },

    handleError: function(errorObj) {
      this.stopPageLoading();
      let errorMsg = null;

      if(errorObj instanceof String) {
        errorMsg = errorObj;
      }
      else if(errorObj) {
        let responseStatus = _.get(errorObj, "response.status");
        let errorMessage = errorObj.message || '';
        if (responseStatus === 401 || errorMessage.includes("status code 401") ||
            responseStatus === 403 || errorMessage.includes("status code 403")
        ) {
          errorMsg = "This session is invalid or expired. Please close this window.";
        }
        else {
          let responseResult = _.get(errorObj, "response.data.result");
          if (_.isString(responseResult)) {
            let applicationErrorPrefix = "UI_ERROR:";

            if (responseResult.startsWith(applicationErrorPrefix)) {
              errorMsg = responseResult.substring(applicationErrorPrefix.length, responseResult.length);
            }
          }
        }
      }

      if (!errorMsg) {
        console.log(errorObj);
        errorMsg = "An unexpected error has occurred.";
      }

      this.setError(errorMsg);
      KTUtil.scrollTop();
    },

    startPageLoading: function() {
      this.$store.dispatch(ADD_BODY_CLASSNAME, "page-loading");
    },

    stopPageLoading: function() {
      this.$store.dispatch(REMOVE_BODY_CLASSNAME, "page-loading");
    }
  },

  computed: {
    wizardPages: function () {
      return [
        { pageNumber: 1, title: 'POLICY OVERVIEW', id: 'overviewPage' },
        { pageNumber: 2, title: 'INCLUSIONS', id: 'inclusionsPage' },
        { pageNumber: 3, title: 'NOTES', id: 'notesPage' },
        { pageNumber: 3, title: 'TASKS', id: 'tasksPage' },
        { pageNumber: 4, title: 'DOCUMENTS', id: 'documentsPage' },
      ];
    }
  }
};
</script>
